import {
  useCallback,
  useEffect,
  type MutableRefObject,
  type RefObject,
} from 'react';

const LOAD_OLDER_MESSAGES_THRESHOLD_PX = 80;

type UseLoadOlderChatMessagesOptions = {
  containerRef: RefObject<HTMLDivElement | null>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoadingOlderMessagesRef: MutableRefObject<boolean>;
  fetchNextPage: () => Promise<unknown>;
  contentVersion: number;
  isReady: boolean;
};

function canLoadOlderMessages({
  containerRef,
  hasNextPage,
  isFetchingNextPage,
  isLoadingOlderMessagesRef,
}: Pick<
  UseLoadOlderChatMessagesOptions,
  | 'containerRef'
  | 'hasNextPage'
  | 'isFetchingNextPage'
  | 'isLoadingOlderMessagesRef'
>): boolean {
  return (
    Boolean(containerRef.current) &&
    hasNextPage &&
    !isFetchingNextPage &&
    !isLoadingOlderMessagesRef.current
  );
}

export function useLoadOlderChatMessages({
  containerRef,
  hasNextPage,
  isFetchingNextPage,
  isLoadingOlderMessagesRef,
  fetchNextPage,
  contentVersion,
  isReady,
}: UseLoadOlderChatMessagesOptions) {
  const loadOlderMessages = useCallback(() => {
    const container = containerRef.current;
    if (
      !canLoadOlderMessages({
        containerRef,
        hasNextPage,
        isFetchingNextPage,
        isLoadingOlderMessagesRef,
      }) ||
      !container
    ) {
      return;
    }

    isLoadingOlderMessagesRef.current = true;
    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;

    void fetchNextPage().finally(() => {
      requestAnimationFrame(() => {
        const nextContainer = containerRef.current;
        if (nextContainer) {
          const nextScrollHeight = nextContainer.scrollHeight;
          nextContainer.scrollTop =
            previousScrollTop + (nextScrollHeight - previousScrollHeight);
        }
        isLoadingOlderMessagesRef.current = false;
      });
    });
  }, [
    containerRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoadingOlderMessagesRef,
  ]);

  const handleScrollNearTop = useCallback(() => {
    const container = containerRef.current;
    if (
      !canLoadOlderMessages({
        containerRef,
        hasNextPage,
        isFetchingNextPage,
        isLoadingOlderMessagesRef,
      }) ||
      !container
    ) {
      return;
    }

    if (container.scrollTop > LOAD_OLDER_MESSAGES_THRESHOLD_PX) {
      return;
    }

    loadOlderMessages();
  }, [
    containerRef,
    hasNextPage,
    isFetchingNextPage,
    isLoadingOlderMessagesRef,
    loadOlderMessages,
  ]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const container = containerRef.current;
    if (
      !canLoadOlderMessages({
        containerRef,
        hasNextPage,
        isFetchingNextPage,
        isLoadingOlderMessagesRef,
      }) ||
      !container
    ) {
      return;
    }

    const isScrollable = container.scrollHeight > container.clientHeight + 1;

    if (!isScrollable) {
      loadOlderMessages();
    }
  }, [
    containerRef,
    contentVersion,
    hasNextPage,
    isFetchingNextPage,
    isLoadingOlderMessagesRef,
    isReady,
    loadOlderMessages,
  ]);

  return { handleScrollNearTop };
}
